import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Download,
  CheckCircle,
  Error as ErrorIcon,
  Edit,
  Warning,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import EditRowModal from "./EditRowModal";

const requiredColumns = [
  "category",
  "subcategory",
  "name",
  "source",
  "shortdescription",
  "hyperlink",
];

const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "-";
  // Assuming dateStr is in YYYY-MM-DD format
  const [yyyy, mm, dd] = dateStr.split("-");
  if (yyyy && mm && dd) {
    return `${dd}-${mm}-${yyyy}`;
  }
  return dateStr;
};

// Sample categories and subcategories - replace with actual data from API
const categories = ["Technology", "Science", "Engineering", "Mathematics"];
const subCategories = {
  Technology: [
    "AI/ML",
    "Cloud Computing",
    "Cybersecurity",
    "Web Development",
  ],
  Science: ["Biology", "Chemistry", "Physics", "Astronomy"],
  Engineering: ["Civil", "Mechanical", "Electrical", "Computer"],
  Mathematics: ["Pure Math", "Applied Math", "Statistics", "Geometry"],
};

const validateData = (data) => {
  const errors = [];

  data.forEach((row, index) => {
    const rowErrors = [];

    // Check required fields
    if (!row["Name"] || row["Name"].toString().trim() === "") {
      rowErrors.push("Name is required");
    }
    if (!row["Category"] || row["Category"].toString().trim() === "") {
      rowErrors.push("Category is required");
    }
    if (!row["Subcategory"] || row["Subcategory"].toString().trim() === "") {
      rowErrors.push("Subcategory is required");
    }
    if (!row["Source"] || row["Source"].toString().trim() === "") {
      rowErrors.push("Source is required");
    }
    if (
      !row["Short Description"] ||
      row["Short Description"].toString().trim() === ""
    ) {
      rowErrors.push("Short Description is required");
    }
    if (!row["Hyperlink"] || row["Hyperlink"].toString().trim() === "") {
      rowErrors.push("Hyperlink is required");
    }

    // Validate Recurring field
    if (row["Recurring"]) {
      const recurring = row["Recurring"].toString().toUpperCase().trim();
      if (
        recurring !== "Y" &&
        recurring !== "N" &&
        recurring !== "YES" &&
        recurring !== "NO"
      ) {
        rowErrors.push("Recurring must be Y/N or Yes/No");
      }
    }

    if (rowErrors.length > 0) {
      errors.push({
        row: index + 1,
        errors: rowErrors,
      });
    }
  });
  return errors;
};

const ExcelUploader = ({ onUploadComplete, showSnackbar }) => {
  const [excelData, setExcelData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [duplicateErrors, setDuplicateErrors] = useState([]);
  const [fileName, setFileName] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editingRowIndex, setEditingRowIndex] = useState(null);

  const processExcelFile = useCallback((file) => {
    setLoading(true);
    setError("");
    setValidationErrors([]);
    setDuplicateErrors([]);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          raw: false,
          header: 1,
          blankrows: false,
        });

        if (jsonData.length <= 1) {
          setError("The Excel file is empty or has no data rows");
          setLoading(false);
          return;
        }

        const headers = jsonData[0];
        const dataRows = jsonData.slice(1);

        const processedData = dataRows.map((row, rowIndex) => {
          const rowObj = {};
          headers.forEach((header, colIndex) => {
            let value = row[colIndex] !== undefined ? row[colIndex] : "";
            const normalizedHeader = header
              .toString()
              .trim()
              .replace(/\s+/g, "")
              .toLowerCase();
            let standardKey = null;

            if (normalizedHeader === "category") {
              standardKey = "Category";
            } else if (normalizedHeader === "subcategory") {
              standardKey = "Subcategory";
            } else if (normalizedHeader === "name") {
              standardKey = "Name";
            } else if (normalizedHeader === "source") {
              standardKey = "Source";
            } else if (normalizedHeader === "shortdescription") {
              standardKey = "Short Description";
            } else if (normalizedHeader.includes("hyperlink")) {
              standardKey = "Hyperlink";
            } else if (
              normalizedHeader.includes("lastDateToApply") ||
              normalizedHeader.includes("lastDateToApplyDate")
            ) {
              standardKey = "Last Date to Apply";
            } else if (normalizedHeader.includes("recurring")) {
              standardKey = "Recurring";
            } else if (normalizedHeader.includes("approved")) {
              standardKey = "Approved";
            } else if (normalizedHeader.includes("feature")) {
              standardKey = "Feature";
            } else if (
              normalizedHeader.includes("internalcomments") ||
              normalizedHeader.includes("comments")
            ) {
              standardKey = "Internal Comments";
            }

            if (standardKey === "Hyperlink") {
              const cellAddress = XLSX.utils.encode_cell({
                r: rowIndex + 1,
                c: colIndex,
              });
              const cell = worksheet[cellAddress];
              if (cell && cell.l) {
                value = cell.l.Target || cell.l.href || value;
              }
            }

            if (standardKey) {
              rowObj[standardKey] = value;
            } else {
              rowObj[header] = value;
            }
          });
          return rowObj;
        });

        const normalizedHeaders = headers.map((header) =>
          header.toString().trim().replace(/\s+/g, "").toLowerCase()
        );

        const missingColumns = requiredColumns.filter(
          (col) => !normalizedHeaders.includes(col)
        );

        if (missingColumns.length > 0) {
          setError(`Missing required columns: ${missingColumns.join(", ")}`);
          setLoading(false);
          return;
        }

        processedData.forEach((row) => {
          let recVal = row["Recurring"];
          if (recVal === "TRUE" || recVal === true) {
            row["Recurring"] = "Yes";
          } else if (recVal === "FALSE" || recVal === false) {
            row["Recurring"] = "No";
          }

          let appVal = row["Approved"];
          if (appVal === "TRUE" || appVal === true) {
            row["Approved"] = "Yes";
          } else if (appVal === "FALSE" || appVal === false) {
            row["Approved"] = "No";
          }

          let featVal = row["Feature"];
          if (featVal === "TRUE" || featVal === true) {
            row["Feature"] = "Yes";
          } else if (featVal === "FALSE" || featVal === false) {
            row["Feature"] = "No";
          }
          if (row["Last Date to Apply"]) {
            let dateStr = row["Last Date to Apply"].toString().trim();

            // Handle DD-MM-YYYY or DD/MM/YYYY
            const ddmmyyyy = /^(\d{2})[-/](\d{2})[-/](\d{4})$/;
            if (ddmmyyyy.test(dateStr)) {
              const [, dd, mm, yyyy] = dateStr.match(ddmmyyyy);
              row["Last Date to Apply"] = `${yyyy}-${mm}-${dd}`;
            }
            // Handle if Excel gives MM/DD/YY or Date object
            else {
              const parsed = new Date(dateStr);
              if (!isNaN(parsed)) {
                const yyyy = parsed.getFullYear();
                const mm = String(parsed.getMonth() + 1).padStart(2, "0");
                const dd = String(parsed.getDate()).padStart(2, "0");
                row["Last Date to Apply"] = `${yyyy}-${mm}-${dd}`;
              }
            }
          }
        });

        const errors = validateData(processedData);
        if (errors.length > 0) {
          setValidationErrors(errors);
        }

        setExcelData(processedData);
        setFileName(file.name);
        setShowPreview(true);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(
          "Error reading Excel file. Please ensure it is a valid Excel file."
        );
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setError("Failed to read file");
      setLoading(false);
    };

    reader.readAsArrayBuffer(file);
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];

      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];

      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(xlsx|xls|csv)$/)
      ) {
        setError("Please upload a valid Excel file (.xlsx, .xls, or .csv)");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        return;
      }

      processExcelFile(file);
    }
  }, [processExcelFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearData = () => {
    setExcelData([]);
    setValidationErrors([]);
    setDuplicateErrors([]);
    setError("");
    setFileName("");
    setShowPreview(false);
    setPage(0);
  };

  const handleDeleteRow = (index) => {
    const newData = excelData.filter((_, i) => i !== index);
    setExcelData(newData);

    // Remove errors for the deleted row
    setValidationErrors(
      validationErrors.filter((err) => err.row !== index + 1)
    );
    setDuplicateErrors(duplicateErrors.filter((err) => err.row !== index + 1));

    // Adjust pagination if necessary
    if (newData.length === 0) {
      setShowPreview(false);
      setPage(0);
    } else if (page * rowsPerPage >= newData.length) {
      setPage(Math.max(0, Math.floor((newData.length - 1) / rowsPerPage)));
    }
  };

  const handleEditRow = (row, index) => {
    setEditingRow(row);
    setEditingRowIndex(index);
    setEditModalOpen(true);
  };

  const handleUpdateRow = (index, updatedData) => {
    const newData = [...excelData];
    newData[index] = updatedData;
    setExcelData(newData);

    // Re-validate after update
    const errors = validateData(newData);
    setValidationErrors(errors);

    // Clear duplicate errors for this row if fixed
    const newDuplicateErrors = duplicateErrors.filter(
      (err) => err.row !== index + 1
    );
    setDuplicateErrors(newDuplicateErrors);
  };

  const handleUpload = async () => {
    if (validationErrors.length > 0) {
      setError("Please fix validation errors before uploading");
      return;
    }

    setUploading(true);
    setDuplicateErrors([]);

    try {
      const transformedData = excelData.map((row) => {
        let lastDateToApply = row["Last Date to Apply"] || "";
        // Convert DD-MM-YYYY to YYYY-MM-DD for API
        if (lastDateToApply) {
          const [dd, mm, yyyy] = lastDateToApply.split("-");
          lastDateToApply = `${yyyy}-${mm}-${dd}`;
        }
        return {
          category: row["Category"],
          subCategory: row["Subcategory"],
          name: row["Name"],
          source: row["Source"],
          shortDescription: row["Short Description"],
          hyperlink: row["Hyperlink"],
          lastDateToApply,
          recurring: row["Recurring"],
          approved: row["Approved"],
          feature: row["Feature"],
          internalComments: row["Internal Comments"],
        };
      });

      const response = await onUploadComplete(transformedData);

      // Check for duplicate errors in response
      if (response?.data?.data && Array.isArray(response.data.data)) {
        const duplicates = response.data.data
          .filter((item) => !item.inserted && item.reason === "already exists")
          .map((item) => ({
            row: item.index + 1,
            reason: item.reason,
            name: excelData[item.index]?.Name,
          }));

        if (duplicates.length > 0) {
          setDuplicateErrors(duplicates);
          setError(
            `${duplicates.length} row(s) already exist in the database. Please edit or remove duplicate entries.`
          );
          setUploading(false);
          return;
        }
      }

      if (response && response.success) {
        showSnackbar(`Successfully uploaded ${excelData.length} opportunities`);
        handleClearData();
      }
    } catch (err) {
      setError("Failed to upload data. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        Category: "Technology",
        Subcategory: "AI/ML",
        Name: "Sample Research Grant",
        Source: "Government Agency",
        "Short Description": "Research grant for AI projects",
        Hyperlink: "https://example.com",
        "Last Date to Apply": "13-11-2025",
        Recurring: "Yes",
        Approved: "Yes",
        Feature: "Yes",
        "Internal Comments": "Sample comment",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "research_opportunities_template.xlsx");
  };

  const paginatedData = excelData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getRowError = (index) => {
    const validationError = validationErrors.find(
      (err) => err.row === index + 1
    );
    const duplicateError = duplicateErrors.find((err) => err.row === index + 1);

    if (duplicateError) return { type: "duplicate", error: duplicateError };
    if (validationError) return { type: "validation", error: validationError };
    return null;
  };

  const totalErrors = validationErrors.length + duplicateErrors.length;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#1e3a5f", mb: 0.5 }}
            >
              Upload Resources and Opportunities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload Excel file with resources and opportunities data
            </Typography>
          </Box>
          <Button
            startIcon={<Download />}
            variant="outlined"
            onClick={downloadTemplate}
            size="small"
            sx={{
              borderColor: "#1e3a5f",
              color: "#1e3a5f",
              "&:hover": {
                borderColor: "#152d4a",
                bgcolor: "rgba(30, 58, 95, 0.04)",
              },
            }}
          >
            Download Template
          </Button>
        </Box>

        {!showPreview ? (
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "#1e3a5f" : "grey.400",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: isDragActive
                ? "rgba(30, 58, 95, 0.05)"
                : "background.paper",
              transition: "all 0.3s",
              "&:hover": {
                borderColor: "#1e3a5f",
                bgcolor: "rgba(30, 58, 95, 0.02)",
              },
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload
              sx={{ fontSize: 60, color: "#1e3a5f", mb: 2, opacity: 0.8 }}
            />
            <Typography variant="h6" gutterBottom sx={{ color: "#1e3a5f" }}>
              {isDragActive
                ? "Drop the file here"
                : "Drag & drop Excel file here"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse files
            </Typography>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 2, color: "text.secondary" }}
            >
              Supported formats: .xlsx, .xls, .csv (Max 5MB)
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                gap: 2,
                p: 2,
                bgcolor: "rgba(76, 175, 80, 0.08)",
                borderRadius: 2,
                border: "1px solid rgba(76, 175, 80, 0.2)",
              }}
            >
              <CheckCircle sx={{ color: "success.main" }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {fileName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {excelData.length} rows loaded
                </Typography>
              </Box>
              <Tooltip title="Remove file">
                <IconButton onClick={handleClearData} color="error">
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 2, borderRadius: 2 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Found {validationErrors.length} validation error(s):
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              {validationErrors.slice(0, 5).map((err, idx) => (
                <li key={idx}>
                  <Typography variant="body2">
                    Row {err.row}: {err.errors.join(", ")}
                  </Typography>
                </li>
              ))}
              {validationErrors.length > 5 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  ... and {validationErrors.length - 5} more errors
                </Typography>
              )}
            </Box>
          </Alert>
        )}

        {duplicateErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Found {duplicateErrors.length} duplicate entry/entries:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, mb: 0 }}>
              {duplicateErrors.slice(0, 5).map((err, idx) => (
                <li key={idx}>
                  <Typography variant="body2">
                    Row {err.row}: "{err.name}" already exists in the database
                  </Typography>
                </li>
              ))}
              {duplicateErrors.length > 5 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  ... and {duplicateErrors.length - 5} more duplicates
                </Typography>
              )}
            </Box>
          </Alert>
        )}
      </Paper>

      {showPreview && excelData.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "rgba(30, 58, 95, 0.02)",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#1e3a5f" }}
              >
                Preview Data
              </Typography>
              {totalErrors > 0 && (
                <Typography variant="body2" color="error">
                  {totalErrors} row(s) with errors - fix before uploading
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={
                uploading ? <CircularProgress size={20} /> : <CloudUpload />
              }
              onClick={handleUpload}
              disabled={uploading || totalErrors > 0}
              sx={{
                bgcolor: "#1e3a5f",
                "&:hover": {
                  bgcolor: "#152d4a",
                },
              }}
            >
              {uploading ? "Uploading..." : "Upload Data"}
            </Button>
          </Box>

          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    S.No
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Actions
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Subcategory
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#f5f5f5",
                      minWidth: 200,
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Source
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#f5f5f5",
                      minWidth: 200,
                    }}
                  >
                    Short Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#f5f5f5",
                      minWidth: 150,
                    }}
                  >
                    Hyperlink
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    Last Date to Apply
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Recurring
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Approved
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", bgcolor: "#f5f5f5" }}>
                    Feature
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#f5f5f5",
                      minWidth: 150,
                    }}
                  >
                    Comments
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index) => {
                  const actualRowIndex = page * rowsPerPage + index;
                  const rowError = getRowError(actualRowIndex);
                  const hasError = rowError !== null;

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: hasError
                          ? rowError.type === "duplicate"
                            ? "rgba(211, 47, 47, 0.08)"
                            : "rgba(255, 152, 0, 0.08)"
                          : "inherit",
                        "&:hover": {
                          bgcolor: hasError
                            ? rowError.type === "duplicate"
                              ? "rgba(211, 47, 47, 0.12)"
                              : "rgba(255, 152, 0, 0.12)"
                            : "action.hover",
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {actualRowIndex + 1}
                          {hasError && (
                            <Tooltip
                              title={
                                rowError.type === "duplicate"
                                  ? "Duplicate entry"
                                  : rowError.error.errors.join(", ")
                              }
                            >
                              {rowError.type === "duplicate" ? (
                                <ErrorIcon color="error" fontSize="small" />
                              ) : (
                                <Warning color="warning" fontSize="small" />
                              )}
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Edit row">
                            <IconButton
                              size="small"
                              onClick={() => handleEditRow(row, actualRowIndex)}
                              sx={{
                                color: "#1e3a5f",
                                "&:hover": {
                                  bgcolor: "rgba(30, 58, 95, 0.1)",
                                },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete row">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRow(actualRowIndex)}
                              sx={{
                                color: "error.main",
                                "&:hover": {
                                  bgcolor: "rgba(211, 47, 47, 0.1)",
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{row["Category"]}</TableCell>
                      <TableCell>{row["Subcategory"]}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {row["Name"]}
                        </Typography>
                      </TableCell>
                      <TableCell>{row["Source"]}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 200 }}
                        >
                          {row["Short Description"]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row["Hyperlink"] && (
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 150 }}
                          >
                            {row["Hyperlink"]}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 150 }}
                        >
                          {formatDateForDisplay(row["Last Date to Apply"])}
                        </Typography>
                      </TableCell>
                      <TableCell>{row["Recurring"]}</TableCell>
                      <TableCell>{row["Approved"]}</TableCell>
                      <TableCell>{row["Feature"]}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 150 }}
                        >
                          {row["Internal Comments"]}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={excelData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Edit Row Modal */}
      <EditRowModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingRow(null);
          setEditingRowIndex(null);
        }}
        rowData={editingRow}
        rowIndex={editingRowIndex}
        onUpdate={handleUpdateRow}
        categories={categories}
        subCategories={subCategories}
      />
    </Box>
  );
};

export default ExcelUploader;
